import { useCallback, useContext, useState, useEffect } from 'react';
import {
    Box,
    Button,
    Flex,
    Grid,
    Heading,
    Spinner,
    Text,
} from 'theme-ui';
import { Field } from '~/Forms/Field';
import { FieldArray, Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { getOnboardingStatusAsync, institutionalOnboardingDirectors } from 'features/auth/authSlice';
import { getProfileOptionsAsync } from 'features/theme/themeSlice'
import { toast } from 'utils/toast';
import { connect, useDispatch } from 'react-redux';
import { Yup } from 'utils/validationRules';
import { InstitutionalOnboardingContext } from '../InstitutionalLanding';
import { Accordion } from '~/Common';
import { toTitleCase } from 'utils/helpers';
import { Icon } from 'assets/Icon';
import { Submit } from '~/Forms';

const mapStateToProps = (state) => {
    return { user: state.user, theme: state.theme }
}

const validationSchema = Yup.object().shape({
    users: Yup.array().of(
        Yup.object().shape({
            title: Yup.string().required(),
            first_name: Yup.string().required(),
            last_name: Yup.string().required(),
            mobile: Yup.string().nullable(),
            email: Yup.string().email().required(),
            is_director: Yup.boolean().required(),
            is_signatory: Yup.boolean().required(),
        })
    ),
    sole_director: Yup.boolean().required(),
    applicant_is_director: Yup.boolean().required(),
    applicant_is_signatory: Yup.boolean().required(),
})
.test('at-least-one-director', 'There must be at least one director (applicant or user)', function (values) {
    const { applicant_is_director, users } = values;
    const directorCount = (applicant_is_director ? 1 : 0) + (users ? users.filter(user => user.is_director).length : 0);
    return directorCount >= 1;
})
.test('sole-director-check', 'There can be only one director if sole_director is true', function (values) {
    const { sole_director, applicant_is_director, users } = values;
    if (sole_director) {
        const directorCount = (applicant_is_director ? 1 : 0) + (users ? users.filter(user => user.is_director).length : 0);
        return directorCount === 1;
    }
    return true;
});

const InstitutionalOnboardingPersonal = ({ user, theme }) => {
    const onboarding = useContext(InstitutionalOnboardingContext);
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [profileOptions, setProfileOptions] = useState(theme.profile.options);

    const getOptions = useCallback(() => {
        if (theme.profile.status === 'loading') {
            dispatch(getProfileOptionsAsync());
        }
    }, [theme, dispatch]);

    useEffect(() => {
        getOptions();

        if (theme.profile.status === 'attempted') {
            setProfileOptions(theme.profile.options);
            setLoading(false);
        }

    }, [theme, getOptions]);

    const createDirector = (values, push) => {
        push({
            title: '',
            first_name: '',
            last_name: '',
            mobile: '',
            email: '',
            is_director: false,
            is_signatory: false,
        });
    };

    const handleSubmit = (values, actions) => {
        setLoading(true);
        institutionalOnboardingDirectors({
            users: values.users,
            sole_director: values.sole_director,
            applicant_is_director: values.applicant_is_director,
            applicant_is_signatory: values.applicant_is_signatory,
        }).then((response) => {
            toast.success(t('onboarding.institutional.landing.personal.success'), {
                toastId: 'personal-success',
            });
            onboarding.nextStep();
            dispatch(getOnboardingStatusAsync());
            setLoading(false);
        }).catch(({ response }) => {
            response?.data?.errors && actions.setErrors(response.data.errors);
            toast.error(response?.data?.message || t('onboarding.institutional.landing.personal.error'), {
                toastId: 'personal-error',
            });
            actions.setStatus('api_error');
            actions.setSubmitting(false);
            setLoading(false);
        });
    };

    return (
        <>
            {loading && <Spinner variant="styles.spinner" m={'auto'} />}

            <Box sx={{
                display: loading ? 'none' : 'block'
            }}>
                <Formik
                    enableReinitialize
                    initialValues={{
                        users: user?.user?.institution?.users?.filter(institutionalUser => institutionalUser.uuid !== user?.user?.uuid) ?? [],
                        sole_director: user?.user?.institution?.sole_director,
                        applicant_is_director: user?.user?.institution?.users?.some(institutionalUser => institutionalUser.uuid === user?.user?.uuid && institutionalUser.is_director),
                        applicant_is_signatory: user?.user?.institution?.users?.some(institutionalUser => institutionalUser.uuid === user?.user?.uuid && institutionalUser.is_signatory),
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values }) => (
                        <FormContent
                            values={values}
                            profileOptions={profileOptions}
                            createDirector={createDirector}
                            user={user.user}
                        />
                    )}                    
                </Formik>
            </Box>
        </>
    )
};

const FormContent = ({ values, profileOptions, createDirector, user }) => {
    const { t } = useTranslation();
    const hasDirectors = Boolean(Array.isArray(values?.users) && values?.users?.length > 0);

    return (
        <Box mb={5}>
            <Form>
                <Heading as="h1" variant="h3" sx={{ fontWeight: 500 }} mb={4}>
                    {t('onboarding.institutional.landing.directors.title')}
                </Heading>

                <Heading as="h2" variant="form_section" mb={3} sx={{
                    fontWeight: 700,
                    fontSize: 2
                }}>
                    {t('onboarding.institutional.landing.directors.subtitle')}
                </Heading>

                <Grid
                    mb={4}
                    sx={{
                        gridGap: [0, 0, 0, 1],
                        gridTemplateColumns: ['1fr'],
                        width: '100%',
                        maxWidth: '432px',
                    }}
                >
                    <Field label="I am a Director" name="applicant_is_director" field="checkbox" value={true} checked={values.applicant_is_director} validIndicator={false} />
                    <Field label="I am an Authorised Signatory" name="applicant_is_signatory" field="checkbox" value={true} checked={values.applicant_is_signatory} validIndicator={false} />
                </Grid>

                <Heading as="h2" variant="form_section" mb={3} sx={{
                    fontWeight: 700,
                    fontSize: 2
                }}>
                    {t('Additional Directors & Authorised Signatories')}
                </Heading>

                <Field label="Company has a sole director" name="sole_director" field="checkbox" value={true} checked={values.sole_director} validIndicator={false} />
                <Text mb={3} as="p">{t('fieldsRequired')}</Text>
                <FieldArray name="users">
                    {({ remove, push }) => (
                        <>
                            {hasDirectors ? (
                                values.users.map((institutionalUser, index) => (
                                    institutionalUser.email !== user.email ? (
                                        <Accordion
                                        title={toTitleCase(`${institutionalUser.first_name ?? ''} ${institutionalUser.last_name ?? ''}`.trim())}
                                        isOpen={true}
                                        emptyTitle={t('New Director / Authorised Signatory')}
                                        key={`users.${index}`}
                                        >
                                            <Flex mt={[3, 0]} sx={{ justifyContent: 'space-around', flexDirection: ['column', 'row'] }}>
                                                <Field label="Director" name={`users.${index}.is_director`} field="checkbox" value={true} checked={values.users[index].is_director} validIndicator={false} />
                                                <Field label="Authorised Signatory" name={`users.${index}.is_signatory`} field="checkbox" value={true} checked={values.users[index].is_signatory} validIndicator={false} />
                                            </Flex>

                                            <Field label="Title *" placeholder="Select title" name={`users.${index}.title`} field="searchable-select" options={profileOptions.titles} required={true} />
                                            <Field label="First Name *" name={`users.${index}.first_name`} updateOnBlur={true} />

                                            <Field label="Last Name *" name={`users.${index}.last_name`} updateOnBlur={true} />

                                            <Field label="Mobile" name={`users.${index}.mobile`} field="phone" updateOnBlur={true} />
                                            <Field label="Email *" name={`users.${index}.email`} type="email" updateOnBlur={true} />

                                            <Box sx={{ mb: 4, fontSize: '13px', pt: 1 }}>
                                                <Text sx={{ color: 'dark' }}>{t('Email will be used for correspondence via Docusign for the application forms')}</Text>
                                            </Box>

                                            <Button
                                                mt={4}
                                                onClick={() => remove(index)}
                                                size="small"
                                                sx={{ py: 1, px: 2, fontSize: 0 }}
                                            >
                                                {t('Remove')}
                                            </Button>
                                        </Accordion>
                                    ) : null
                                ))
                            ) : (
                                <Box mb={3}></Box>
                            )}

                            <Box>

                                <Button
                                    sx={{
                                        display: ['flex'],
                                        alignItems: 'center',
                                        width: '100%',
                                        mb: 2
                                    }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        createDirector(values, push);
                                    }}
                                >
                                    <Icon icon="plus" size="12" mr={2} />
                                    {t('Add Additional Director / Authorised Signatory')}
                                </Button>
                            </Box>
                        </>
                    )}
                </FieldArray>

                <Submit variant="primary" id="directorsSubmit" text={t('onboarding.institutional.landing.directors.submit')} sx={{ display: 'block', width: '100%' }} mt={4} />
            </Form>
        </Box>
    );
};

export default connect(mapStateToProps, null)(InstitutionalOnboardingPersonal);